import { Mat4, Quat, Vec3 } from 'playcanvas';

import { PlacePivotOp, EntityTransformOp, MultiOp, SetPivotOffsetOp } from './edit-ops';
import { ElementType } from './element';
import { Events } from './events';
import { Mesh } from './mesh';
import { Pivot } from './pivot';
import { Splat } from './splat';
import { Transform } from './transform';
import { TransformHandler } from './transform-handler';

const mat = new Mat4();
const quat = new Quat();
const transform = new Transform();
const tmpOffset = new Vec3();
const tmpBase = new Vec3();
const tmpDelta = new Vec3();
const tmpInvRot = new Quat();

class EntityTransformHandler implements TransformHandler {
    events: Events;
    element: any;
    top: EntityTransformOp;
    pop: PlacePivotOp;
    bindMat = new Mat4();

    private _onPivotStarted: (pivot: Pivot) => void;
    private _onPivotMoved: (pivot: Pivot) => void;
    private _onPivotEnded: (pivot: Pivot) => void;
    private _onPivotOrigin: (mode: 'center' | 'boundCenter') => void;
    private _onFocalPointPicked: (details: { splat: Splat; position: Vec3 }) => void;
    private _onPivotChanged: (element: unknown) => void;

    constructor(events: Events) {
        this.events = events;

        this._onPivotStarted = (_pivot: Pivot) => {
            if (this.element && events.invoke('tool.active') !== 'measure') {
                this.start();
            }
        };

        this._onPivotMoved = (pivot: Pivot) => {
            if (this.element && events.invoke('tool.active') !== 'measure') {
                this.update(pivot.transform);
            }
        };

        this._onPivotEnded = (_pivot: Pivot) => {
            if (this.element && events.invoke('tool.active') !== 'measure') {
                this.end();
            }
        };

        this._onPivotOrigin = (_mode: 'center' | 'boundCenter') => {
            if (this.element) {
                this.placePivot();
            }
        };

        this._onPivotChanged = (element: unknown) => {
            // Only refresh the active selection's pivot.
            if (!this.element || element !== this.element) return;
            const pivot = this.events.invoke('pivot') as any;
            if (pivot?.active) return;
            this.placePivot();
        };

        this._onFocalPointPicked = (details: { splat: Splat; position: Vec3 }) => {
            // Treat a picked focal point as a pivot placement intent while transform tools are active.
            // Instead of moving only the gizmo, persist an element-local pivotOffset so rotate/scale
            // occur about that point (and it survives save/load).
            if (!this.element || !['move', 'rotate', 'scale'].includes(this.events.invoke('tool.active'))) {
                return;
            }

            const sel = this.element as any;
            const entity = sel?.pivot ?? sel?.entity ?? sel;
            if (!entity?.getLocalPosition || !entity?.getLocalRotation || !entity?.getLocalScale) {
                return;
            }

            // Compute the base pivot (without offset) in the same space as the picked point.
            // NOTE: In this repo, elements live under scene.contentRoot so local == world.
            if (sel?.type === ElementType.splat) {
                const origin = this.events.invoke('pivot.origin');
                (sel as Splat).getPivot(origin === 'center' ? 'center' : 'boundCenter', false, transform);
                tmpBase.copy(transform.position);
            } else {
                tmpBase.copy(entity.getLocalPosition());
            }

            // Solve for pivotOffset in element local axes such that:
            //   picked = base + (R * (S * pivotOffset))
            // => pivotOffset = S^-1 * (R^-1 * (picked - base))
            tmpDelta.sub2(details.position, tmpBase);
            tmpInvRot.copy(entity.getLocalRotation()).invert();
            tmpInvRot.transformVector(tmpDelta, tmpOffset);
            const scl = entity.getLocalScale();
            tmpOffset.set(
                scl.x !== 0 ? tmpOffset.x / scl.x : 0,
                scl.y !== 0 ? tmpOffset.y / scl.y : 0,
                scl.z !== 0 ? tmpOffset.z / scl.z : 0
            );

            const oldOffset = sel.pivotOffset?.clone ? sel.pivotOffset.clone() : new Vec3(sel.pivotOffset?.x ?? 0, sel.pivotOffset?.y ?? 0, sel.pivotOffset?.z ?? 0);
            const newOffset = new Vec3(tmpOffset.x, tmpOffset.y, tmpOffset.z);

            const op = new SetPivotOffsetOp({ element: sel, oldOffset, newOffset });
            this.events.fire('edit.add', op);
        };

        events.on('pivot.started', this._onPivotStarted);
        events.on('pivot.moved', this._onPivotMoved);
        events.on('pivot.ended', this._onPivotEnded);
        events.on('pivot.origin', this._onPivotOrigin);
        events.on('pivot.changed', this._onPivotChanged);
        events.on('camera.focalPointPicked', this._onFocalPointPicked);
    }

    destroy() {
        this.events.off('pivot.started', this._onPivotStarted);
        this.events.off('pivot.moved', this._onPivotMoved);
        this.events.off('pivot.ended', this._onPivotEnded);
        this.events.off('pivot.origin', this._onPivotOrigin);
        this.events.off('pivot.changed', this._onPivotChanged);
        this.events.off('camera.focalPointPicked', this._onFocalPointPicked);
    }

    placePivot() {
        // For splats, place pivot at either center or boundCenter.
        // For other elements, use their current transform.
        const sel = this.element as any;
        if (sel?.type === ElementType.splat) {
            const origin = this.events.invoke('pivot.origin');
            (sel as Splat).getPivot(origin === 'center' ? 'center' : 'boundCenter', false, transform);
        } else {
            const pos = sel?.pivot?.getLocalPosition?.() ?? sel?.entity?.getLocalPosition?.() ?? sel?.getLocalPosition?.();
            const rot = sel?.pivot?.getLocalRotation?.() ?? sel?.entity?.getLocalRotation?.() ?? sel?.getLocalRotation?.();
            const scl = sel?.pivot?.getLocalScale?.() ?? sel?.entity?.getLocalScale?.() ?? sel?.getLocalScale?.();
            transform.set(pos ?? new Vec3(), rot ?? new Quat(), scl ?? new Vec3(1, 1, 1));
        }

        // Apply element-local pivotOffset as a world/parent-space translation.
        // pivotOffset is stored in element local axes, so we must convert it by the element's
        // current rotation+scale (translation is ignored).
        try {
            const offset = sel?.pivotOffset as Vec3 | undefined;
            if (offset && (offset.x !== 0 || offset.y !== 0 || offset.z !== 0)) {
                const entity = sel?.pivot ?? sel?.entity ?? sel;
                if (entity?.getLocalTransform) {
                    entity.getLocalTransform().transformVector(offset, tmpOffset);
                    transform.position.add(tmpOffset);
                }
            }
        } catch (e) {
            // best effort
        }
        this.events.invoke('pivot').place(transform);
    }

    activate() {
        this.element = this.events.invoke('selection') as Splat | Mesh | any;
        if (this.element) {
            this.placePivot();
        }
    }

    deactivate() {
        this.element = null;
    }

    start() {
        const pivot = this.events.invoke('pivot') as Pivot;
        const { transform } = pivot;
        const sel = this.element as any;
        const entity = sel?.pivot ?? sel?.entity ?? sel;

        // calculate bind matrix
        this.bindMat.setTRS(transform.position, transform.rotation, transform.scale);
        this.bindMat.invert();
        this.bindMat.mul2(this.bindMat, entity.getLocalTransform());

        const p = entity.getLocalPosition();
        const r = entity.getLocalRotation();
        const s = entity.getLocalScale();

        // create op
        this.top = new EntityTransformOp({
            // EntityTransformOp currently expects { splat }, but we use it as a generic TRS op.
            // @ts-ignore
            splat: this.element,
            oldt: new Transform(p, r, s),
            newt: new Transform(p, r, s)
        });

        this.pop = new PlacePivotOp({
            pivot,
            oldt: transform.clone(),
            newt: transform.clone()
        });
    }

    update(transform: Transform) {
        mat.setTRS(transform.position, transform.rotation, transform.scale);
        mat.mul2(mat, this.bindMat);
        quat.setFromMat4(mat);

        const t = mat.getTranslation();
        const r = quat;
        const s = mat.getScale();

        // route to selected element's move() so it can map TRS into its domain (eg clipBox halfExtents)
        if (this.element?.move) {
            this.element.move(t, r, s);
        }
        this.top.newt.set(t, r, s);
        this.pop.newt.copy(transform);
    }

    end() {
        // if anything changed then register the op with undo/redo system
        const { oldt, newt } = this.top;

        if (!oldt.equals(newt)) {
            this.events.fire('edit.add', new MultiOp([this.top, this.pop]));
        }

        this.top = null;
        this.pop = null;
    }
}

export { EntityTransformHandler };
